import React from "react";
import axios from "axios";
import { domainDetails } from "./domainDetails";

const AppContext = React.createContext(null);

export function useAppContext() {
    return React.useContext(AppContext);
}

// tracking-config lambda (HTTP API "proship_tracking_config", id o28gs00ncf). Accepts ?host=<host>
// and returns the display-safe config. Default = PROD stage; non-prod Amplify branches (and local)
// override via the REACT_APP_TRACKING_CONFIG_API build-time env var (e.g. the /stage/config endpoint).
const TRACKING_CONFIG_API =
    process.env.REACT_APP_TRACKING_CONFIG_API ||
    "https://o28gs00ncf.execute-api.ap-south-1.amazonaws.com/prod/config";

function staticBrand(subdomain) {
    return domainDetails[subdomain] || null;
}

// Dev/staging testing only: a ?host=<host> query param overrides the real host for the config
// lookup, so a brand's tracking page can be opened on the shared Amplify URL without provisioning
// its subdomain. Gated by a build-time flag set in .env.development / .env.staging and absent from
// .env.prod, so it is compiled OUT of the production build entirely (dead regardless of the URL).
const ALLOW_HOST_OVERRIDE = process.env.REACT_APP_ALLOW_HOST_OVERRIDE === "true";
const HOST_OVERRIDE_KEY = "trackingHostOverride";

function resolveHost() {
    if (typeof window === "undefined") {
        return "";
    }
    const real = window.location.host || "";
    if (ALLOW_HOST_OVERRIDE) {
        // The brand host is passed via ?host= for dev/staging testing. The in-app search submits a
        // native GET form that rebuilds the URL without it (and full-reloads), so persist it for the
        // tab session and fall back to the stored value on subsequent pages.
        const fromQuery = new URLSearchParams(window.location.search).get("host");
        if (fromQuery) {
            const h = fromQuery.trim().toLowerCase();
            try { window.sessionStorage.setItem(HOST_OVERRIDE_KEY, h); } catch (e) { /* ignore */ }
            return h;
        }
        try {
            const stored = window.sessionStorage.getItem(HOST_OVERRIDE_KEY);
            if (stored) {
                return stored;
            }
        } catch (e) { /* ignore */ }
    }
    return real;
}

// Normalize the lambda's display-safe config into the domainDetails entry shape the
// components already understand (id, logo, name, webUrl, banner, style + extras).
function apiToBrand(cfg) {
    if (!cfg) {
        return null;
    }
    const large = cfg.bannerLarge || cfg.bannerSmall || null;
    const small = cfg.bannerSmall || cfg.bannerLarge || null;
    return {
        id: cfg.merchantOid || null,
        logo: cfg.logoUrl || null,
        name: cfg.brandName || null,
        webUrl: cfg.webUrl || null,
        banner: large && small ? { large, small } : undefined,
        bannerBgColor: cfg.bannerBgColor || null,
        designTemplate: cfg.designTemplate || null,
        footer: cfg.footer || null,
        instaUrl: cfg.instaUrl || null,
        fbUrl: cfg.fbUrl || null,
        twitterUrl: cfg.twitterUrl || null,
        linkedinUrl: cfg.linkedinUrl || null,
        whatsappNumber: cfg.whatsappNumber || null,
        termsAndConditions: cfg.termsAndConditions || null,
        returnPolicy: cfg.returnPolicy || null,
        privacyPolicy: cfg.privacyPolicy || null,
        source: "api"
    };
}

export function AppContextProvider({ children }) {

    const [values, setValues] = React.useState(null);

    const host = resolveHost();
    const subdomain = host ? host.split(".")[0] : "";

    // CONFIG-FIRST: every subdomain asks the tracking-config lambda for a (KAM-approved, LIVE)
    // config; the hardcoded domainDetails map is only the FALLBACK. The initial state is the
    // static entry (or default) so legacy brands paint their branding instantly with no flash —
    // the API result overrides it only when a live config exists. A 404 (no config / not LIVE),
    // an error, or an old lambda without ?host support all land in catch/no-op → fallback holds.
    const [brand, setBrand] = React.useState(() => staticBrand(subdomain) || domainDetails.default);
    // No lookup at all for: /preview (brand comes from query params) and the generic DEFAULT
    // tracking hosts t./tracking. (permanently non-DIY — they must stay byte-identical to the
    // legacy page, zero added latency or network calls). brandLoading starts FALSE for them so
    // there is not even a single gated render frame.
    const skipLookup = subdomain === "t" || subdomain === "tracking"
        || (typeof window !== "undefined" && window.location.pathname.startsWith("/preview"));
    const [brandLoading, setBrandLoading] = React.useState(!skipLookup);

    // Config-driven brands render the template path, which never mounts the legacy Layout (the
    // component that sets the tab title) — so set it here for them. Fallback brands keep Layout's
    // own untouched title behavior.
    React.useEffect(() => {
        if (brand?.source === "api") {
            document.title = brand.name
                ? `${brand.name} - Order Tracking - Powered By Proship`
                : "Proship Order Tracking";
        }
    }, [brand]);

    React.useEffect(() => {
        if (skipLookup) {
            return undefined;
        }
        let cancelled = false;
        axios
            // timeout bounds the App.js loading gate: a hung lambda/API-GW must degrade to the
            // fallback brand, never leave the page blank indefinitely.
            .get(TRACKING_CONFIG_API, { params: { host }, timeout: 4000 })
            .then((res) => {
                if (cancelled) {
                    return;
                }
                // FAIL-CLOSED: only a config the lambda explicitly marks LIVE is adopted. The
                // current lambda always stamps status on a served page; anything else (older
                // lambda build, unexpected payload) keeps the hardcoded/default fallback.
                const status = res && res.data && res.data.status;
                if (status !== "LIVE") {
                    return;
                }
                const mapped = apiToBrand(res && res.data);
                // merchantOid is MANDATORY: it scopes the track_waybill_p lookup. A config without
                // it must never go config-driven, or the AWB search would run across ALL merchants.
                if (mapped && mapped.id) {
                    setBrand(mapped);
                }
            })
            .catch(() => {
                /* no live config for this host (or timeout) — keep the hardcoded/default fallback */
            })
            .finally(() => {
                if (!cancelled) {
                    setBrandLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [host]);

    return (
        <AppContext.Provider value={{ values, setValues, brand, brandLoading, subdomain }}>
            {children}
        </AppContext.Provider>
    );
}
